import { Button } from "@cofano/cck";
import { Alert, Box, Tooltip } from "@mui/material";
import { CloudQueue, WarningAmberRounded } from "@mui/icons-material";
import { getPluginSettingValue, hasPluginPermission } from "utils";
import { Language } from "imports/Utils";
import { useStoreState, Session } from "ezStore";

export enum MasterdataEntity {
	Ship,
	Fleet,
	Product,
	Location,
	LocationPort,
	LocationTerminal,
	LocationAddress,
	Relation,
	InvoiceGroupType
}

type ExternalMasterdataAlertProps = {
	entity: MasterdataEntity,
	externalId: string | null | undefined,
	detailMode?: undefined
} | {
	entity: MasterdataEntity.Fleet,
	externalId: string | null | undefined,
	detailMode: boolean | undefined
};

const ExternalMasterdataAlert = ({ entity, externalId, detailMode }: ExternalMasterdataAlertProps) => {
	const session = useStoreState(state => state.session);
	const baseUrl = getPluginSettingValue(session, "externalMasterdataBaseURL");
	const url = getUrl(baseUrl, entity, externalId, detailMode);
	const shouldShow = shouldShowAlert(entity, session);

	if (!shouldShow) {
		return <></>;
	}

	return <Alert
		severity="error"
		style={{ marginBottom: 16 }}
		action={url && <Button
			variant="outlined"
			size="small"
			icon={<CloudQueue fontSize="small" />}
			onClick={() => window.open(url, "_blank")?.focus()}
		>
			{Language("EXTERNAL_MASTERDATA_OPEN")}
		</Button>}
	>
		{Language("EXTERNAL_MASTERDATA_ALERT")}
	</Alert>;
};

export const ExternalMasterdataWarningIcon = ({ entity }: { entity: MasterdataEntity }) => {
	const session = useStoreState(state => state.session);

	if (!shouldShowAlert(entity, session)) {
		return <></>;
	}

	return <Tooltip title={Language("EXTERNAL_MASTERDATA_ALERT")} placement="top">
		<Box display="flex" alignItems="center">
			<WarningAmberRounded color="error" />
		</Box>
	</Tooltip>;
};

const getUrl = (baseUrl: string | undefined, entity: MasterdataEntity, externalId: string | undefined | null, detailMode: boolean | undefined) => {
	if (!baseUrl) {
		return null;
	}

	const target = baseUrl.includes("stack") ? "stack" : "bos";
	if (baseUrl.endsWith("/")) {
		baseUrl = baseUrl.slice(0, -1); // remove trailing /
	}

	const isNew = !externalId || externalId === "0";

	switch (entity) {
		case MasterdataEntity.Ship:
			switch (target) {
				case "bos": return isNew
					? `${baseUrl}/view/ships/create?tab=general`
					: `${baseUrl}/view/ships/${externalId}/edit?tab=general`;
				case "stack": return null;
			}
			break;
		case MasterdataEntity.Fleet:
			switch (target) {
				case "bos":
					return detailMode
						? `${baseUrl}/view/fleets/${externalId}/details`
						: isNew
							? `${baseUrl}/view/fleets/create`
							: `${baseUrl}/view/fleets/${externalId}/edit`;
				case "stack":
					return null;
			}
			break;
		case MasterdataEntity.Product:
			switch (target) {
				case "bos": return isNew
					? `${baseUrl}/view/products/create?tab=nstr`
					: `${baseUrl}/view/products/${externalId}/edit?tab=nstr`;
				case "stack": return null;
			}
			break;
		case MasterdataEntity.Location:
		case MasterdataEntity.LocationPort:
		case MasterdataEntity.LocationTerminal:
		case MasterdataEntity.LocationAddress:
			switch (target) {
				case "bos": return isNew
					? `${baseUrl}/view/locations/create?tab=details`
					: `${baseUrl}/view/locations/${externalId}/edit?tab=details`;
				case "stack": switch (entity) {
					case MasterdataEntity.LocationTerminal: return isNew
						? `${baseUrl}/masterdata/terminals/create`
						: `${baseUrl}/masterdata/terminals/edit/${externalId}`;
					case MasterdataEntity.LocationPort: return isNew
						? `${baseUrl}/masterdata/ports/create`
						: `${baseUrl}/masterdata/ports/edit/${externalId}`;
					case MasterdataEntity.LocationAddress: return isNew
						? `${baseUrl}/masterdata/purelocations/create`
						: `${baseUrl}/masterdata/purelocations/edit/${externalId}`;
					default: return null;
				}
			}
			break;
		case MasterdataEntity.Relation:
			switch (target) {
				case "bos": return isNew
					? `${baseUrl}/view/relations/create?tab=general`
					: `${baseUrl}/view/relations/${externalId}/edit?tab=general`;
				case "stack": return isNew
					? `${baseUrl}/relations/create`
					: `${baseUrl}/relations/edit/${externalId}`;
			}
			break;
		case MasterdataEntity.InvoiceGroupType:
			switch (target) {
				case "bos" : return isNew
					? `${baseUrl}/view/invoicegrouptypes/create`
					: `${baseUrl}/view/invoicegrouptypes/${externalId}/edit`;
				case "stack":
					return null;
			}
			break;
	}
};

const shouldShowAlert = (entity: MasterdataEntity, session: Session) => {
	switch (entity) {
		case MasterdataEntity.Ship:
		case MasterdataEntity.Fleet:
			return hasPluginPermission(session, "externalFleetManagement");
		case MasterdataEntity.Product:
			return hasPluginPermission(session, "externalProductManagement");
		case MasterdataEntity.Location:
		case MasterdataEntity.LocationPort:
		case MasterdataEntity.LocationTerminal:
		case MasterdataEntity.LocationAddress:
			return hasPluginPermission(session, "externalLocationManagement");
		case MasterdataEntity.Relation:
			return hasPluginPermission(session, "externalRelationManagement");
		case MasterdataEntity.InvoiceGroupType:
			return hasPluginPermission(session, "receiveMasterdataSyncEvents");
		default:
			return false;
	}
};

export default ExternalMasterdataAlert;
